class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.emptyStateNode = this.predictiveSearchResults?.querySelector('[data-predictive-search-empty-state]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.onWindowScroll = this.onWindowScroll.bind(this);
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    window.addEventListener('resize', this.onWindowScroll, { passive: true });

    this.onDocumentMousedown = this.onDocumentMousedown.bind(this);
    document.addEventListener('mousedown', this.onDocumentMousedown);

    this.onPanelClick = this.onPanelClick.bind(this);
    this.predictiveSearchResults?.addEventListener('click', this.onPanelClick);
  }

  onPanelClick(event) {
    const searchForButton = event.target.closest('.predictive-search__item--term, .predictive-search__search-for-button button');
    if (searchForButton) {
      event.preventDefault();
      this.input.form.submit();
    }
  }

  onDocumentMousedown(event) {
    if (!this.isOpen) return;
    if (!this.predictiveSearchResults?.classList.contains('predictive-search--panel')) return;
    if (this.panelContains(event.target)) {
      if (!event.target.closest('a, button, input, [tabindex]:not([tabindex="-1"])')) {
        event.preventDefault();
      }
      return;
    }
    this.close();
  }

  onWindowScroll() {
    if (!this.isOpen) return;
    if (!this.predictiveSearchResults?.classList.contains('predictive-search--panel')) return;
    this.positionPanel();
  }

  positionPanel() {
    const header = document.querySelector('.section-header');
    if (!header) return;
    const headerBottom = Math.max(0, header.getBoundingClientRect().bottom);
    this.headerBottom = headerBottom;
    this.resultsMaxHeight = window.innerHeight - headerBottom;
    this.predictiveSearchResults.style.top = `${headerBottom}px`;
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight}px`;
    this.predictiveSearchResults.style.display = 'block';
  }

  portalPanel() {
    if (!this.predictiveSearchResults) return;
    if (!this.predictiveSearchResults.classList.contains('predictive-search--panel')) return;
    if (this.predictiveSearchResults.parentElement === document.body) return;
    this.panelOriginalParent = this.predictiveSearchResults.parentElement;
    this.panelOriginalNextSibling = this.predictiveSearchResults.nextSibling;
    document.body.appendChild(this.predictiveSearchResults);
  }

  restorePanel() {
    if (!this.predictiveSearchResults) return;
    if (!this.panelOriginalParent) return;
    if (this.panelOriginalNextSibling && this.panelOriginalNextSibling.parentElement === this.panelOriginalParent) {
      this.panelOriginalParent.insertBefore(this.predictiveSearchResults, this.panelOriginalNextSibling);
    } else {
      this.panelOriginalParent.appendChild(this.predictiveSearchResults);
    }
    this.panelOriginalParent = null;
    this.panelOriginalNextSibling = null;
  }

  panelContains(element) {
    if (!element) return false;
    if (this.contains(element)) return true;
    if (this.predictiveSearchResults && this.predictiveSearchResults.contains(element)) return true;
    return false;
  }

  panelQuerySelector(selector) {
    return (this.predictiveSearchResults && this.predictiveSearchResults.querySelector(selector)) || this.querySelector(selector);
  }

  panelQuerySelectorAll(selector) {
    if (this.predictiveSearchResults && this.predictiveSearchResults.contains(this.input) === false) {
      const inPanel = Array.from(this.predictiveSearchResults.querySelectorAll(selector));
      const inHost = Array.from(this.querySelectorAll(selector));
      return inHost.concat(inPanel);
    }
    return Array.from(this.querySelectorAll(selector));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.removeRenderedResults();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.panelQuerySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.panelContains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.panelQuerySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.panelQuerySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = this.panelQuerySelectorAll('li, button.predictive-search__item').filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.panelQuerySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,tag&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
    if (this.predictiveSearchResults?.classList.contains('predictive-search--panel')) {
      this.predictiveSearchResults.setAttribute('data-loading', 'true');
      this.portalPanel();
      this.positionPanel();
    }
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.removeRenderedResults();
    this.predictiveSearchResults.insertAdjacentHTML('beforeend', resultsMarkup);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  removeRenderedResults() {
    this.panelQuerySelector('#predictive-search-results')?.remove();
    this.panelQuerySelector('[data-predictive-search-live-region-count-value]')?.remove();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.predictiveSearchResults?.removeAttribute('data-loading');
    this.setLiveRegionText(this.panelQuerySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const headerBottom = Math.max(0, document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
    this.headerBottom = headerBottom;
    this.resultsMaxHeight = window.innerHeight - headerBottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults?.classList.contains('predictive-search--panel')) {
      this.portalPanel();
      this.positionPanel();
      if (window.innerWidth >= 990) {
        document.body.style.overflow = 'hidden';
      }
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.panelQuerySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.predictiveSearchResults?.removeAttribute('data-loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    document.body.style.overflow = '';
    this.restorePanel();
  }
}

customElements.define('predictive-search', PredictiveSearch);
